import {
  Box,
  Button,
  ExternalLink,
  Spacer,
  Title,
  Spinner,
  SelectField,
  Text,
  TextField,
  useDialogState,
} from '@color/continuum';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { useCallback, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';

import { FixedLoadingIndicator } from '../../common/compoments/FixedLoadingIndicator';
import { useVariantConfirmationsQuery } from '../api';
import {
  ApproveForSendoutDialog,
  isVariantConfirmationStateApprovableForSendout,
} from '../ApproveForSendout';
import { CancellationDialog } from '../Cancellation';
import { CopyableText } from '../CopyableText';
import { useCrossTabQueryInvalidation } from '../crossTabSync';
import { VARIANT_CONFIRMATION_DETAIL_PATH } from '../paths';
import {
  isVariantConfirmationStateRequestable,
  RequestConfirmationDialog,
} from '../RequestConfirmation';
import { VariantConfirmation, VariantConfirmationState, VariantType } from '../types';
import { booleanToYesNo, formatDate } from '../utils';

import {
  AccessionCell,
  GHgvsCell,
  IgvBarcodeLinkCell,
  NotesCell,
  PrimersCell,
  getPrimersCellValue,
} from './columnCells';
import { ImportConfirmationResultsDialog } from './ImportConfirmationResultsDialog';
import { ImportRawDataDialog } from './ImportRawDataDialog';
import { NotesIcon } from './NotesIcon';
import { useHiddenColumns } from './useHiddenColumns';
import {
  ALL_STATES_FILTER_VALUE,
  ALL_VARIANT_TYPES_FILTER_VALUE,
  createPaginationSafetyHandlers,
  ROWS_PER_PAGE_OPTIONS,
  StateFilterOption,
  useListViewUrlState,
  VariantTypeFilterOption,
} from './useListViewUrlState';

export enum Fields {
  ID = 'id',
  ACCESSION_NUMBER = 'accessionNumber',
  SAMPLE = 'sample',
  SEQUENCE_NAME = 'sequenceName',
  GENE = 'gene',
  G_HGVS = 'gHgvs',
  VENDOR_NOTES = 'vendorNotes',
  INTERNAL_NOTES = 'internalNotes',
  PRESENT = 'present',
  TYPE = 'type',
  CLASSIFICATION = 'classification',
  P_LOW_QUALITY = 'pLowQuality',
  EXPECTED_RESULT = 'expectedResult',
  ZYGOSITY = 'zygosity',
  ALL_COMPLETED_STATES = 'allCompletedStates',
  METHOD = 'method',
  VENDOR = 'vendor',
  INCOMPLETE = 'incomplete',
  QUEUED = 'queued',
  REQUESTED = 'requested',
  RESOLVED = 'resolved',
  REVIEWER = 'reviewer',
  FORWARD_PRIMER = 'forwardPrimer',
  REVERSE_PRIMER = 'reversePrimer',
  SANGER_PRIMER = 'sangerPrimer',
  PRIMER_SOURCE = 'primerSource',
  CHROMOSOME = 'chromosome',
  POSITION = 'position',
  SIZE = 'size',
  LOCATION = 'location',
  SIZE_IN_CARRIER = 'sizeInCarrier',
  SANGER_TRACES = 'sangerTraces',
  PRIMERS = 'primers',
  STATE = 'state',
  LAB_ID = 'labId',
}

const STATE_FILTER_OPTIONS = new Set<StateFilterOption>([
  ...Object.values(VariantConfirmationState),
  ALL_STATES_FILTER_VALUE,
]);
const allStateFilterOptionsExcept = (...excluded: StateFilterOption[]) => {
  const s = new Set(STATE_FILTER_OPTIONS);
  excluded.forEach((e) => s.delete(e));
  return s;
};

const COLUMN_VISIBLE_FOR_STATE_FILTER_OPTION: Partial<Record<string, Set<StateFilterOption>>> = {
  [Fields.LAB_ID]: new Set([
    ALL_STATES_FILTER_VALUE,
    VariantConfirmationState.REVIEWABLE,
    VariantConfirmationState.COMPLETED,
    VariantConfirmationState.FAILED,
  ]),
  [Fields.VENDOR_NOTES]: new Set([
    ALL_STATES_FILTER_VALUE,
    VariantConfirmationState.REVIEWABLE,
    VariantConfirmationState.COMPLETED,
    VariantConfirmationState.FAILED,
  ]),
  [Fields.INTERNAL_NOTES]: new Set([
    ALL_STATES_FILTER_VALUE,
    VariantConfirmationState.REVIEWABLE,
    VariantConfirmationState.COMPLETED,
    VariantConfirmationState.FAILED,
    VariantConfirmationState.CANCELLED,
  ]),
  [Fields.PRESENT]: new Set([
    ALL_STATES_FILTER_VALUE,
    VariantConfirmationState.REVIEWABLE,
    VariantConfirmationState.COMPLETED,
  ]),
  [Fields.ZYGOSITY]: new Set([VariantConfirmationState.REVIEWABLE]),
  [Fields.P_LOW_QUALITY]: allStateFilterOptionsExcept(ALL_STATES_FILTER_VALUE),
  [Fields.EXPECTED_RESULT]: allStateFilterOptionsExcept(ALL_STATES_FILTER_VALUE),
  [Fields.ALL_COMPLETED_STATES]: allStateFilterOptionsExcept(ALL_STATES_FILTER_VALUE),
  [Fields.INCOMPLETE]: allStateFilterOptionsExcept(
    ALL_STATES_FILTER_VALUE,
    VariantConfirmationState.NEW,
    VariantConfirmationState.REVIEWABLE
  ),
  [Fields.QUEUED]: allStateFilterOptionsExcept(VariantConfirmationState.REVIEWABLE),
  [Fields.REQUESTED]: allStateFilterOptionsExcept(
    VariantConfirmationState.NEW,
    VariantConfirmationState.REVIEWABLE
  ),
  [Fields.RESOLVED]: new Set([
    ALL_STATES_FILTER_VALUE,
    VariantConfirmationState.COMPLETED,
    VariantConfirmationState.FAILED,
    VariantConfirmationState.CANCELLED,
  ]),
  [Fields.REVIEWER]: new Set([
    ALL_STATES_FILTER_VALUE,
    VariantConfirmationState.COMPLETED,
    VariantConfirmationState.FAILED,
    VariantConfirmationState.CANCELLED,
  ]),
  [Fields.STATE]: new Set([ALL_STATES_FILTER_VALUE]),
  [Fields.PRIMERS]: new Set([VariantConfirmationState.NEW, VariantConfirmationState.QUEUED]),
  [Fields.FORWARD_PRIMER]: new Set([VariantConfirmationState.REVIEWABLE]),
  [Fields.REVERSE_PRIMER]: new Set([VariantConfirmationState.REVIEWABLE]),
  [Fields.SANGER_PRIMER]: new Set([VariantConfirmationState.REVIEWABLE]),
  [Fields.PRIMER_SOURCE]: new Set([VariantConfirmationState.REVIEWABLE]),
  [Fields.CHROMOSOME]: new Set([VariantConfirmationState.REVIEWABLE]),
  [Fields.POSITION]: new Set([VariantConfirmationState.REVIEWABLE]),
  [Fields.SIZE]: new Set([VariantConfirmationState.REVIEWABLE]),
  [Fields.LOCATION]: new Set([VariantConfirmationState.REVIEWABLE]),
  [Fields.SIZE_IN_CARRIER]: new Set([VariantConfirmationState.REVIEWABLE]),
  [Fields.SANGER_TRACES]: new Set([VariantConfirmationState.REVIEWABLE]),
};

export const COLUMNS: GridColDef<VariantConfirmation>[] = [
  {
    field: Fields.ID,
    headerName: 'Id',
    minWidth: 100,
    flex: 1,
    sortable: false,
  },
  {
    field: Fields.ACCESSION_NUMBER,
    headerName: 'Accession #',
    minWidth: 120,
    flex: 1,
    sortable: false,
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <AccessionCell confirmation={confirmation} />
    ),
  },
  {
    field: Fields.SAMPLE,
    headerName: 'Sample',
    minWidth: 170,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => confirmation.sample.kit_barcode,
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <CopyableText text={confirmation.sample.kit_barcode} />
    ),
  },
  {
    field: Fields.SEQUENCE_NAME,
    headerName: 'Sequence Name',
    minWidth: 270,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      confirmation.sample_sequence_run?.barcode ?? '',
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) =>
      confirmation.sample_sequence_run ? (
        <IgvBarcodeLinkCell
          igvLink={confirmation.igv_link}
          barcode={confirmation.sample_sequence_run.barcode}
        />
      ) : null,
  },
  {
    field: Fields.GENE,
    headerName: 'Gene',
    minWidth: 120,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => confirmation.variant.gene,
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <CopyableText text={confirmation.variant.gene} />
    ),
  },
  {
    field: Fields.G_HGVS,
    headerName: 'gHGVS',
    minWidth: 250,
    flex: 1,
    sortable: false,
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <GHgvsCell confirmation={confirmation} />
    ),
  },
  {
    field: Fields.VENDOR_NOTES,
    headerName: 'Vendor Notes',
    minWidth: 200,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => confirmation.vendor_notes,
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <NotesCell label="Vendor Notes" notes={confirmation.vendor_notes} />
    ),
  },
  {
    field: Fields.INTERNAL_NOTES,
    headerName: 'Internal Notes',
    minWidth: 200,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => confirmation.internal_notes,
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <NotesCell label="Internal Notes" notes={confirmation.internal_notes} />
    ),
  },
  {
    field: Fields.PRESENT,
    headerName: 'Present',
    minWidth: 80,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      booleanToYesNo(confirmation.is_present),
  },
  {
    field: Fields.TYPE,
    headerName: 'Type',
    minWidth: 120,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => {
      if (confirmation.is_merged) return 'MNV';
      if (confirmation.variant.is_cnv) return 'SV';
      return 'SNV/Indel';
    },
  },
  {
    field: Fields.CLASSIFICATION,
    headerName: 'Classification',
    minWidth: 170,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => confirmation.variant_classification,
  },
  {
    field: Fields.P_LOW_QUALITY,
    headerName: 'P(Low Quality)',
    minWidth: 130,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => {
      const prediction = confirmation.quality_prediction?.prediction;
      if (prediction == null) return '';
      return `${Math.floor(prediction * 100)}%`;
    },
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => {
      const prediction = confirmation.quality_prediction?.prediction;
      if (prediction == null) return null;
      const isForbidden = !confirmation.quality_prediction?.predicted_to_confirm;
      return (
        <Text variant="body2" color={isForbidden ? 'error' : 'inherit'}>
          {`${Math.floor(prediction * 100)}%`}
        </Text>
      );
    },
  },
  {
    field: Fields.EXPECTED_RESULT,
    headerName: 'Expected Result',
    minWidth: 140,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      booleanToYesNo(confirmation.predicted_to_confirm),
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {booleanToYesNo(confirmation.predicted_to_confirm)}
        {confirmation.prediction_notes && (
          <NotesIcon notes={confirmation.prediction_notes} data-testid="prediction-notes-icon" />
        )}
      </Box>
    ),
  },
  {
    field: Fields.ZYGOSITY,
    headerName: 'Zygosity',
    minWidth: 120,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      confirmation.zygosity === 'Homozygous' ? confirmation.zygosity : '',
  },
  {
    field: Fields.ALL_COMPLETED_STATES,
    headerName: 'All completed states',
    minWidth: 180,
    flex: 1,
    sortable: false,
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {confirmation.all_completed_results.map((item) => (
          <span key={item.result}>
            {item.result.charAt(0).toUpperCase() + item.result.slice(1)} : {item.count}
          </span>
        ))}
      </Box>
    ),
  },
  {
    field: Fields.METHOD,
    headerName: 'Method',
    minWidth: 120,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => confirmation.method,
  },
  {
    field: Fields.VENDOR,
    headerName: 'Vendor',
    minWidth: 120,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => confirmation.vendor,
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {confirmation.vendor}
        {confirmation.vendor_notes &&
          confirmation.state !== VariantConfirmationState.REVIEWABLE && (
            <NotesIcon notes={confirmation.vendor_notes} data-testid="vendor-notes-icon" />
          )}
      </Box>
    ),
  },
  {
    field: Fields.INCOMPLETE,
    headerName: 'Incomplete',
    minWidth: 140,
    flex: 1,
    sortable: false,
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {confirmation.incomplete_confirmations.map((conf) => (
          <ExternalLink
            key={conf.id}
            to={generatePath(VARIANT_CONFIRMATION_DETAIL_PATH, { id: String(conf.id) })}
            onClick={(e) => e.stopPropagation()}
            sx={{ color: 'error.main' }}
          >
            <Text variant="body2" color="inherit">
              {conf.accession_number}
            </Text>
          </ExternalLink>
        ))}
      </Box>
    ),
  },
  {
    field: Fields.QUEUED,
    headerName: 'Queued',
    minWidth: 110,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => formatDate(confirmation.created_at),
  },
  {
    field: Fields.REQUESTED,
    headerName: 'Requested',
    minWidth: 120,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      formatDate(confirmation.requested_at),
  },
  {
    field: Fields.RESOLVED,
    headerName: 'Resolved',
    minWidth: 120,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      formatDate(confirmation.resolved_at),
  },
  {
    field: Fields.REVIEWER,
    headerName: 'Reviewer',
    minWidth: 160,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      confirmation.reviewed_by?.full_name ?? '',
  },
  {
    field: Fields.FORWARD_PRIMER,
    headerName: 'Forward Primer',
    minWidth: 160,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      confirmation.primer_set?.forward_pcr_primer ?? '',
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <CopyableText text={confirmation.primer_set?.forward_pcr_primer ?? ''} />
    ),
  },
  {
    field: Fields.REVERSE_PRIMER,
    headerName: 'Reverse Primer',
    minWidth: 160,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      confirmation.primer_set?.reverse_pcr_primer ?? '',
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <CopyableText text={confirmation.primer_set?.reverse_pcr_primer ?? ''} />
    ),
  },
  {
    field: Fields.SANGER_PRIMER,
    headerName: 'Sanger Primer',
    minWidth: 160,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      confirmation.primer_set?.sanger_sequencing_primer ?? '',
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <CopyableText text={confirmation.primer_set?.sanger_sequencing_primer ?? ''} />
    ),
  },
  {
    field: Fields.PRIMER_SOURCE,
    headerName: 'Primer Source',
    minWidth: 140,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      confirmation.primer_set?.primer_source ?? '',
  },
  {
    field: Fields.CHROMOSOME,
    headerName: 'Chromosome',
    minWidth: 120,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      confirmation.primer_set?.chrom ?? '',
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <CopyableText text={confirmation.primer_set?.chrom ?? ''} />
    ),
  },
  {
    field: Fields.POSITION,
    headerName: 'Position',
    minWidth: 100,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => confirmation.primer_set?.pos ?? '',
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <CopyableText text={confirmation.primer_set?.pos ?? ''} />
    ),
  },
  {
    field: Fields.SIZE,
    headerName: 'Size',
    minWidth: 80,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      confirmation.primer_set?.cnv_size ?? '',
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <CopyableText text={confirmation.primer_set?.cnv_size ?? ''} />
    ),
  },
  {
    field: Fields.LOCATION,
    headerName: 'Location',
    minWidth: 100,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      confirmation.primer_set?.location ?? '',
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <CopyableText text={confirmation.primer_set?.location ?? ''} />
    ),
  },
  {
    field: Fields.SIZE_IN_CARRIER,
    headerName: 'Size in Carrier',
    minWidth: 130,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      confirmation.primer_set?.size_in_carrier ?? '',
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <CopyableText text={confirmation.primer_set?.size_in_carrier ?? ''} />
    ),
  },
  {
    field: Fields.SANGER_TRACES,
    headerName: 'Sanger Traces',
    minWidth: 130,
    flex: 1,
    sortable: false,
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => {
      if (confirmation.sanger_plots_pending) return <Text variant="body2">pending</Text>;
      if (Object.keys(confirmation.sanger_plots).length > 0)
        return <Text variant="body2">view</Text>;
      return null;
    },
  },
  {
    field: Fields.PRIMERS,
    headerName: 'Primers',
    minWidth: 160,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => getPrimersCellValue(confirmation),
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <PrimersCell confirmation={confirmation} />
    ),
  },
  {
    field: Fields.STATE,
    headerName: 'State',
    minWidth: 100,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) =>
      confirmation.state.charAt(0).toUpperCase() + confirmation.state.slice(1),
  },
  {
    field: Fields.LAB_ID,
    headerName: 'Lab ID',
    minWidth: 120,
    flex: 1,
    sortable: false,
    valueGetter: (_value, confirmation: VariantConfirmation) => confirmation.lab_id ?? '',
    renderCell: ({ row: confirmation }: GridRenderCellParams<VariantConfirmation>) => (
      <CopyableText text={confirmation.lab_id ?? ''} />
    ),
  },
];

const DEFAULT_HIDDEN_COLUMNS = [Fields.ID];

const EMPTY_ROW_SELECTION: GridRowSelectionModel = { type: 'include', ids: new Set() };

// The page shows its own FixedLoadingIndicator while fetching, so the grid's
// built-in loading overlay is redundant.
const NoLoadingOverlay = () => null;

export const ListView = () => {
  useCrossTabQueryInvalidation();
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>(EMPTY_ROW_SELECTION);
  const resetSelection = useCallback(() => setRowSelectionModel(EMPTY_ROW_SELECTION), []);

  const {
    dialogIsOpen: requestDialogIsOpen,
    openDialog: openRequestDialog,
    closeDialog: closeRequestDialog,
  } = useDialogState();
  const {
    dialogIsOpen: approveDialogIsOpen,
    openDialog: openApproveDialog,
    closeDialog: closeApproveDialog,
  } = useDialogState();
  const {
    dialogIsOpen: cancelDialogIsOpen,
    openDialog: openCancelDialog,
    closeDialog: closeCancelDialog,
  } = useDialogState();
  const {
    dialogIsOpen: importDialogIsOpen,
    openDialog: openImportDialog,
    closeDialog: closeImportDialog,
  } = useDialogState();
  const {
    dialogIsOpen: importRawDataDialogIsOpen,
    openDialog: openImportRawDataDialog,
    closeDialog: closeImportRawDataDialog,
  } = useDialogState();

  const {
    stateFilter,
    kitBarcodeFilter,
    variantTypeFilter,
    pageNumber,
    rowsPerPage,
    updateFilters,
    setPageNumber,
    setRowsPerPage,
  } = useListViewUrlState({ onChange: resetSelection });

  const visibleColumns = useMemo(
    () =>
      COLUMNS.filter((col) => {
        const allowedStates = COLUMN_VISIBLE_FOR_STATE_FILTER_OPTION[col.field];
        return !allowedStates || allowedStates.has(stateFilter as StateFilterOption);
      }),
    [stateFilter]
  );

  const { columnVisibilityModel, onColumnVisibilityModelChange } = useHiddenColumns({
    storageKey: 'variant_confirmations_columns_hidden',
    allColumns: COLUMNS,
    visibleColumns,
    defaultHiddenColumns: DEFAULT_HIDDEN_COLUMNS,
  });

  const paginationSafety = createPaginationSafetyHandlers({
    pageNumber,
    rowsPerPage,
    setPageNumber,
  });

  const { isLoading, isFetching, data } = useVariantConfirmationsQuery({
    ...(stateFilter !== ALL_STATES_FILTER_VALUE ? { stateFilter } : {}),
    ...(variantTypeFilter !== ALL_VARIANT_TYPES_FILTER_VALUE ? { variantTypeFilter } : {}),
    ...(kitBarcodeFilter ? { kitBarcodeFilter } : {}),
    pageNumber,
    rowsPerPage,
    onPageNumberChange: setPageNumber,
    onRowsPerPageChange: setRowsPerPage,
    onQuerySuccess: paginationSafety.onSuccess,
    onQueryError: paginationSafety.onError,
  });

  const confirmations = useMemo(() => data?.results || [], [data]);
  const totalRowCount = data?.count ?? 0;

  const selectedConfirmations = useMemo(
    () => confirmations.filter((c) => rowSelectionModel.ids.has(c.id)),
    [confirmations, rowSelectionModel]
  );
  const hasSelectedConfirmations = selectedConfirmations.length > 0;

  const showRequestButton =
    stateFilter !== ALL_STATES_FILTER_VALUE && isVariantConfirmationStateRequestable(stateFilter);

  const showApproveForSendoutButton =
    stateFilter !== ALL_STATES_FILTER_VALUE &&
    isVariantConfirmationStateApprovableForSendout(stateFilter);

  const showCancelButton = stateFilter === VariantConfirmationState.NEW;
  const showImportButton = stateFilter === VariantConfirmationState.PENDING;
  const showImportRawDataButton = stateFilter === VariantConfirmationState.PENDING;

  const onRowClick = useCallback((params: GridRowParams<VariantConfirmation>) => {
    // Allow drag-to-select text without triggering navigation.
    if (window.getSelection()?.toString()) return;
    window.open(
      generatePath(VARIANT_CONFIRMATION_DETAIL_PATH, { id: String(params.id) }),
      '_blank',
      'noopener,noreferrer'
    );
  }, []);

  if (!data && isLoading) {
    // only on initial loading
    return (
      <>
        <FixedLoadingIndicator />
        <Spinner />
      </>
    );
  }

  return (
    <>
      {isFetching && <FixedLoadingIndicator />}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 40,
        }}
      >
        <Title>Variant Confirmations</Title>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {showRequestButton && (
            <Button
              variant="contained"
              size="small"
              disabled={!hasSelectedConfirmations}
              onClick={openRequestDialog}
            >
              Request
            </Button>
          )}
          {showApproveForSendoutButton && (
            <Button
              variant="contained"
              size="small"
              disabled={!hasSelectedConfirmations}
              onClick={openApproveDialog}
            >
              Approve for sendout
            </Button>
          )}
          {showCancelButton && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={!hasSelectedConfirmations}
              onClick={openCancelDialog}
            >
              Cancel
            </Button>
          )}
          {showImportButton && (
            <Button variant="contained" size="small" onClick={openImportDialog}>
              Import confirmation result
            </Button>
          )}
          {showImportRawDataButton && (
            <Button variant="contained" size="small" onClick={openImportRawDataDialog}>
              Import raw data
            </Button>
          )}
        </Box>
      </Box>
      <Spacer variant="small" />

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box sx={{ width: '200px' }}>
          <SelectField
            label="State"
            options={[
              { label: 'New', value: VariantConfirmationState.NEW },
              { label: 'Queued', value: VariantConfirmationState.QUEUED },
              { label: 'Pending', value: VariantConfirmationState.PENDING },
              {
                label: 'Reviewable',
                value: VariantConfirmationState.REVIEWABLE,
              },
              { label: 'Completed', value: VariantConfirmationState.COMPLETED },
              { label: 'Failed', value: VariantConfirmationState.FAILED },
              { label: 'Cancelled', value: VariantConfirmationState.CANCELLED },
              { label: 'All', value: ALL_STATES_FILTER_VALUE },
            ]}
            includeEmptyOption={false}
            optionalIndicatorIsEnabled={false}
            value={stateFilter}
            onChange={(e) => updateFilters({ state: e.target.value as StateFilterOption })}
          />
        </Box>
        <Box sx={{ width: '140px' }}>
          <SelectField
            label="Type"
            options={[
              { label: 'All', value: ALL_VARIANT_TYPES_FILTER_VALUE },
              { label: 'MNV', value: VariantType.MNV },
              { label: 'SV', value: VariantType.SV },
              { label: 'SNV/Indel', value: VariantType.SNV_INDEL },
            ]}
            includeEmptyOption={false}
            optionalIndicatorIsEnabled={false}
            value={variantTypeFilter}
            onChange={(e) =>
              updateFilters({ variantType: e.target.value as VariantTypeFilterOption })
            }
          />
        </Box>
        <Box>
          <TextField
            label="Kit Barcode"
            name="kitBarcode"
            sx={{ width: '220px' }}
            value={kitBarcodeFilter}
            optionalIndicatorIsEnabled={false}
            onChange={(e) => updateFilters({ kitBarcode: e.target.value as string })}
          />
        </Box>
      </Box>

      <Spacer variant="small" />

      <DataGrid
        sx={(theme) => ({
          '& .MuiDataGrid-columnHeader': {
            color: theme.typography.body1.color,
            fontSize: theme.typography.body1.fontSize,
            fontFamily: theme.typography.fontFamily,
            backgroundColor: theme.palette.background.lightBg,
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            minHeight: 50,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: theme.palette.primary.hover,
          },
        })}
        loading={isFetching}
        slots={{ loadingOverlay: NoLoadingOverlay }}
        autoHeight
        getRowHeight={() => 'auto'}
        checkboxSelection
        disableRowSelectionOnClick
        columns={visibleColumns}
        rows={confirmations}
        rowCount={totalRowCount}
        pagination
        paginationMode="server"
        paginationModel={{ page: pageNumber, pageSize: rowsPerPage }}
        pageSizeOptions={[...ROWS_PER_PAGE_OPTIONS]}
        onPaginationModelChange={(model) => {
          if (model.page !== pageNumber) {
            setPageNumber(model.page);
          }
          if (model.pageSize !== rowsPerPage) {
            setRowsPerPage(model.pageSize);
          }
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        disableRowSelectionExcludeModel
        onRowClick={onRowClick}
      />

      {requestDialogIsOpen && (
        <RequestConfirmationDialog
          confirmations={selectedConfirmations}
          onClose={closeRequestDialog}
          onSuccess={() => {
            closeRequestDialog();
            setRowSelectionModel(EMPTY_ROW_SELECTION);
          }}
        />
      )}
      {approveDialogIsOpen && (
        <ApproveForSendoutDialog
          confirmations={selectedConfirmations}
          dialogVariant="batch"
          onClose={closeApproveDialog}
          onSuccess={() => {
            closeApproveDialog();
            setRowSelectionModel(EMPTY_ROW_SELECTION);
          }}
        />
      )}
      {cancelDialogIsOpen && (
        <CancellationDialog
          confirmations={selectedConfirmations}
          onClose={closeCancelDialog}
          onSuccess={() => {
            closeCancelDialog();
            setRowSelectionModel(EMPTY_ROW_SELECTION);
          }}
        />
      )}
      {importDialogIsOpen && (
        <ImportConfirmationResultsDialog
          onClose={closeImportDialog}
          onSuccess={closeImportDialog}
        />
      )}
      {importRawDataDialogIsOpen && (
        <ImportRawDataDialog
          onClose={closeImportRawDataDialog}
          onSuccess={closeImportRawDataDialog}
        />
      )}
    </>
  );
};
